/**
 * Stage 73.20t — Approval Workflow Configuration page.
 *
 * Admin defines a single workflow per document type
 * (expense_claim_sheet | loan). Each level can match either a system
 * role (manager / hr_admin / finance_reviewer / payroll_admin /
 * tenant_admin) OR a list of specific named users (or both).
 *
 * Sequential — Level N unlocks only after Level N-1 is approved.
 */
import React, { useEffect, useMemo, useState } from 'react';
import MainLayout from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Plus, Trash2, Save, Loader2, ArrowUp, ArrowDown,
  Workflow, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DOC_TYPE_LABEL = {
  expense_claim_sheet: 'Expense Claim Sheets',
  loan: 'Advances',
};
const ROLE_LABEL = {
  manager: 'Reporting Manager',
  hr_admin: 'HR Admin',
  payroll_admin: 'Payroll Admin',
  finance_reviewer: 'Finance Reviewer',
  tenant_admin: 'Tenant Admin',
};

export default function ApprovalWorkflows() {
  const [meta, setMeta] = useState({ doc_types: [], approver_roles: [], departments: [] });
  const [activeDocType, setActiveDocType] = useState('expense_claim_sheet');
  // Stage 73.20ch — Department scope: '' = tenant-wide (default fallback),
  // or a specific department_id from Masters.
  const [activeDept, setActiveDept] = useState('');
  const [workflows, setWorkflows] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);    // for named-user picker

  useEffect(() => { loadMeta(); loadUsers(); }, []);
  useEffect(() => {
    if (meta.doc_types.length) loadWorkflows();
    /* eslint-disable-next-line */
  }, [activeDocType, activeDept, meta.doc_types.length]);

  const loadMeta = async () => {
    try {
      const m = await axios.get(`${API}/approval-workflows/_meta`, { withCredentials: true });
      setMeta({
        doc_types: Array.isArray(m.data?.doc_types) ? m.data.doc_types : [],
        approver_roles: Array.isArray(m.data?.approver_roles) ? m.data.approver_roles : [],
        departments: Array.isArray(m.data?.departments) ? m.data.departments : [],
      });
    } catch (e) {
      toast.error('Meta load failed');
    }
  };

  const loadWorkflows = async () => {
    setLoading(true);
    try {
      const deptParam = activeDept ? `&department_id=${activeDept}` : '&department_id=null';
      const w = await axios.get(`${API}/approval-workflows?doc_type=${activeDocType}${deptParam}`, { withCredentials: true });
      const list = Array.isArray(w.data) ? w.data : [];
      setWorkflows(list);
      const active = list.find(x => x.active);
      setLevels(Array.isArray(active?.levels)
        ? active.levels.map((l, i) => ({ ...l, _key: `${i}-${l.name}` }))
        : []);
    } catch (e) {
      setWorkflows([]); setLevels([]);
      toast.error('Load failed: ' + (e?.response?.data?.detail || e.message));
    }
    setLoading(false);
  };

  const loadUsers = async () => {
    try {
      const u = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers((u.data || []).map(x => ({ id: x.id, name: x.name || x.email, email: x.email, role: x.role })));
    } catch (e) { /* optional */ }
  };

  const addLevel = () => {
    setLevels([...levels, {
      _key: `new-${Date.now()}`,
      order: levels.length + 1,
      name: '', approver_role: 'manager',
      approver_user_ids: [],
      required: true, notes: '',
    }]);
  };

  const updateLevel = (idx, patch) => {
    setLevels(levels.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };

  const removeLevel = (idx) => {
    setLevels(levels.filter((_, i) => i !== idx).map((l, i) => ({ ...l, order: i + 1 })));
  };

  const moveLevel = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= levels.length) return;
    const copy = [...levels];
    [copy[idx], copy[ni]] = [copy[ni], copy[idx]];
    setLevels(copy.map((l, i) => ({ ...l, order: i + 1 })));
  };

  const save = async () => {
    if (levels.length === 0) {
      toast.error('Add at least one approval level');
      return;
    }
    for (const l of levels) {
      if (!l.name?.trim()) {
        toast.error('Each level needs a name (e.g. HOD, CFO, Director)');
        return;
      }
      if (!l.approver_role && (!l.approver_user_ids || l.approver_user_ids.length === 0)) {
        toast.error(`Level "${l.name}" needs a role OR at least one named approver`);
        return;
      }
    }
    setSaving(true);
    try {
      await axios.post(`${API}/approval-workflows`, {
        doc_type: activeDocType,
        name: activeDept ? `Dept-${activeDept.slice(-6)}` : 'Default',
        department_id: activeDept || null,
        levels: levels.map((l, i) => ({
          order: i + 1, name: l.name.trim(),
          approver_role: l.approver_role || null,
          approver_user_ids: l.approver_user_ids || [],
          required: l.required !== false,
          notes: l.notes || '',
        })),
        active: true,
      }, { withCredentials: true });
      const scopeLabel = activeDept
        ? (meta.departments.find(d => d.id === activeDept)?.name || 'this department')
        : 'all departments (default fallback)';
      toast.success(`Workflow saved — applies to ${scopeLabel} for ${DOC_TYPE_LABEL[activeDocType]}.`);
      await loadWorkflows();
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <MainLayout>
      <div className="p-6 space-y-6" data-testid="approval-workflows-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Workflow className="w-6 h-6 text-blue-600" />
              Approval Workflows
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Define the approval chain (HOD → CFO → Director, etc.) per document type. Sequential — each level unlocks only after the previous one approves.
            </p>
          </div>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wf-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save Workflow
          </Button>
        </div>

        {/* Doc type tabs */}
        <div className="flex flex-wrap gap-2">
          {meta.doc_types.map(t => (
            <button
              key={t}
              onClick={() => setActiveDocType(t)}
              className={`px-4 py-2 rounded-full text-sm border ${activeDocType === t ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300'}`}
              data-testid={`wf-tab-${t}`}
            >
              {DOC_TYPE_LABEL[t] || t}
            </button>
          ))}
        </div>

        {/* Stage 73.20ch — Department scope selector. Pick a department
            to define a department-specific approval chain; leave on
            "All Departments (Default)" for the tenant-wide fallback. */}
        <div className="flex flex-wrap items-center gap-3 bg-blue-50/60 border border-blue-200 rounded-lg p-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-900">Department Scope</span>
          <select
            data-testid="wf-dept-select"
            value={activeDept}
            onChange={(e) => setActiveDept(e.target.value)}
            className="text-sm bg-white border-2 border-blue-300 rounded-md px-3 py-1.5"
          >
            <option value="">All Departments (Default fallback)</option>
            {meta.departments?.map(d => (
              <option key={d.id} value={d.id}>{`${d.name}${d.code ? ` (${d.code})` : ''}`}</option>
            ))}
          </select>
          <span className="text-[11px] text-blue-800">
            {activeDept
              ? '→ This workflow applies ONLY to employees in this department. Tenant-wide default is used for everyone else.'
              : '→ Tenant-wide default. Applies to every employee whose department has no specific workflow.'}
          </span>
        </div>

        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">
              Chain — {DOC_TYPE_LABEL[activeDocType]} ({levels.length} level{levels.length === 1 ? '' : 's'})
            </h3>
            <Button variant="outline" size="sm" onClick={addLevel} data-testid="wf-add-level">
              <Plus className="w-3 h-3 mr-1" /> Add Level
            </Button>
          </div>

          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          ) : levels.length === 0 ? (
            <p className="text-xs text-slate-500 italic text-center py-12">
              No approval chain configured yet. Click "Add Level" to add the first approver (e.g. HOD).<br />
              Submissions without a workflow continue to follow the default ad-hoc approval.
            </p>
          ) : (
            <div className="space-y-2" data-testid="wf-levels-list">
              {levels.map((lvl, idx) => (
                <LevelRow
                  key={lvl._key}
                  level={lvl}
                  index={idx}
                  total={levels.length}
                  meta={meta}
                  users={users}
                  onUpdate={(patch) => updateLevel(idx, patch)}
                  onRemove={() => removeLevel(idx)}
                  onMoveUp={() => moveLevel(idx, -1)}
                  onMoveDown={() => moveLevel(idx, +1)}
                />
              ))}
            </div>
          )}
        </Card>

        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-[11px] text-blue-900">
          <p className="font-semibold mb-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> How approval works:</p>
          <ul className="list-disc ml-5 space-y-0.5">
            <li>When an employee submits, the workflow is snapshotted onto the document.</li>
            <li>Each level can match by <b>role</b> (any user with that role) OR by <b>named users</b> (only those individuals).</li>
            <li>Approval is sequential — Level 2 only unlocks after Level 1 approves.</li>
            <li>Any rejection sets the document to <b>rejected</b> and the employee must re-submit.</li>
            <li>The PDF shows each approver's name + date + remarks once their level signs off.</li>
            <li>Tenant Admins can approve any level as a global override.</li>
          </ul>
        </div>
      </div>
    </MainLayout>
  );
}

function LevelRow({ level, index, total, meta, users, onUpdate, onRemove, onMoveUp, onMoveDown }) {
  const [userQuery, setUserQuery] = useState('');
  const matchedUsers = useMemo(() =>
    users.filter(u =>
      !level.approver_user_ids?.includes(u.id) &&
      (u.name?.toLowerCase().includes(userQuery.toLowerCase()) ||
       u.email?.toLowerCase().includes(userQuery.toLowerCase()))
    ).slice(0, 8)
  , [users, userQuery, level.approver_user_ids]);

  const selectedUsers = users.filter(u => level.approver_user_ids?.includes(u.id));

  return (
    <div className="border border-slate-200 rounded p-3 bg-white" data-testid={`wf-level-row-${index}`}>
      <div className="flex items-start gap-2">
        <div className="flex flex-col items-center pt-1">
          <button onClick={onMoveUp} disabled={index === 0} className="text-slate-400 hover:text-blue-600 disabled:opacity-30">
            <ArrowUp className="w-3 h-3" />
          </button>
          <span className="font-mono text-[10px] text-slate-500 my-1">#{index + 1}</span>
          <button onClick={onMoveDown} disabled={index === total - 1} className="text-slate-400 hover:text-blue-600 disabled:opacity-30">
            <ArrowDown className="w-3 h-3" />
          </button>
        </div>
        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] text-slate-500">Level Name *</label>
            <input
              value={level.name || ''}
              onChange={e => onUpdate({ name: e.target.value })}
              placeholder="HOD / CFO / Director / Finance Head"
              className="w-full border border-slate-300 rounded px-2 py-1 text-xs mt-0.5"
              data-testid={`wf-level-name-${index}`}
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-500">Match By Role</label>
            <select
              value={level.approver_role || ''}
              onChange={e => onUpdate({ approver_role: e.target.value || null })}
              className="w-full border border-slate-300 rounded px-2 py-1 text-xs mt-0.5"
              data-testid={`wf-level-role-${index}`}
            >
              <option value="">— (no role match) —</option>
              {meta.approver_roles?.map(r => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500">Named Approvers (optional)</label>
            <div className="border border-slate-300 rounded p-1 mt-0.5 min-h-[28px] flex flex-wrap items-center gap-1">
              {selectedUsers.map(u => (
                <span key={u.id} className="inline-flex items-center gap-0.5 bg-blue-100 text-blue-800 text-[10px] rounded px-1.5 py-0.5">
                  {u.name}
                  <button onClick={() => onUpdate({ approver_user_ids: (level.approver_user_ids || []).filter(x => x !== u.id) })} className="ml-0.5 hover:text-rose-600">×</button>
                </span>
              ))}
              <input
                value={userQuery}
                onChange={e => setUserQuery(e.target.value)}
                placeholder="Search name…"
                className="flex-1 outline-none text-xs bg-transparent min-w-[80px]"
                data-testid={`wf-level-user-search-${index}`}
              />
            </div>
            {userQuery && matchedUsers.length > 0 && (
              <div className="absolute z-10 bg-white border border-slate-200 rounded shadow mt-1 max-h-32 overflow-y-auto">
                {matchedUsers.map(u => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => { onUpdate({ approver_user_ids: [...(level.approver_user_ids || []), u.id] }); setUserQuery(''); }}
                    className="block w-full text-left px-2 py-1 text-xs hover:bg-blue-50"
                    data-testid={`wf-level-user-opt-${index}-${u.id}`}
                  >
                    {u.name} <span className="text-slate-400 text-[10px]">· {u.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <button onClick={onRemove} className="text-rose-500 hover:text-rose-700 ml-2" data-testid={`wf-level-remove-${index}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      <input
        value={level.notes || ''}
        onChange={e => onUpdate({ notes: e.target.value })}
        placeholder="Optional notes for this level"
        className="w-full border border-slate-200 rounded px-2 py-1 text-[10px] mt-2 text-slate-600"
      />
    </div>
  );
}
